import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface PtoRequest {
  id: number
  user: { id: number; name: string } | null
  request_type: number
  request_type_name: string
  start_date: string | null
  end_date: string | null
  hours_requested: number
  hours_approved: number
  effective_hours: number
  full_day: boolean
  reason: string | null
  notes: string | null
  status: number
  status_name: string
  submitted_at: string | null
  approved_at: string | null
  approved_by: { id: number; name: string } | null
  approval_notes: string | null
  duration_days: number | null
}

interface Props {
  pto_request: PtoRequest
  can_approve: boolean
  can_edit: boolean
}

export default function Show({ pto_request, can_approve, can_edit }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "PTO Requests", href: "/pto_requests" },
    { title: "Details", href: `/pto_requests/${pto_request.id}` },
  ]

  const formatDateTime = (date: string | null) => {
    if (!date) return "-"
    return new Date(date).toLocaleString()
  }

  const statusBadge = (status: number, statusName: string) => {
    const colors: Record<number, string> = {
      0: "bg-hpi-yellow/30 text-hpi-dark-gray",
      1: "bg-hpi-dark-green/20 text-hpi-dark-green",
      2: "bg-hpi-red/20 text-hpi-red",
      99: "bg-hpi-pale-gray text-hpi-dark-gray",
    }
    return (
      <span
        className={`rounded px-3 py-1 text-sm ${colors[status] || "bg-hpi-pale-gray text-hpi-dark-gray"}`}
      >
        {statusName}
      </span>
    )
  }

  const handleApprove = () => {
    router.patch(`/pto_requests/${pto_request.id}/approve`)
  }

  const handleDeny = () => {
    const notes = prompt("Enter reason for denial (optional):")
    router.patch(`/pto_requests/${pto_request.id}/deny`, {
      approval_notes: notes,
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`PTO Request - ${pto_request.request_type_name}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-semibold">PTO Request Details</h1>
            {statusBadge(pto_request.status, pto_request.status_name)}
          </div>
          <div className="space-x-2">
            {can_edit && pto_request.status === 0 && (
              <Link
                href={`/pto_requests/${pto_request.id}/edit`}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
              >
                Edit
              </Link>
            )}
            {can_approve && pto_request.status === 0 && (
              <>
                <button
                  onClick={handleApprove}
                  className="bg-hpi-dark-green hover:bg-hpi-dark-green/80 rounded px-4 py-2 text-white"
                >
                  Approve
                </button>
                <button
                  onClick={handleDeny}
                  className="bg-hpi-red hover:bg-hpi-red/80 rounded px-4 py-2 text-white"
                >
                  Deny
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* Request Info */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Request Information</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Employee</dt>
                <dd>{pto_request.user?.name ?? "-"}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Type</dt>
                <dd>{pto_request.request_type_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Start Date</dt>
                <dd>{formatDate(pto_request.start_date)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">End Date</dt>
                <dd>{formatDate(pto_request.end_date)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Duration</dt>
                <dd>
                  {pto_request.duration_days !== null
                    ? `${pto_request.duration_days} day(s)`
                    : "-"}
                </dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Full Day</dt>
                <dd>{pto_request.full_day ? "Yes" : "No"}</dd>
              </div>
            </dl>
          </div>

          {/* Hours */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Hours</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Hours Requested</dt>
                <dd>{pto_request.hours_requested.toFixed(2)}</dd>
              </div>
              {pto_request.status === 1 && (
                <>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">Hours Approved</dt>
                    <dd>{pto_request.hours_approved.toFixed(2)}</dd>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <dt className="font-semibold">Effective Hours</dt>
                    <dd className="text-hpi-dark-green font-semibold">
                      {pto_request.effective_hours.toFixed(2)}
                    </dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {/* Reason */}
          {pto_request.reason && (
            <div className="rounded-lg bg-white p-6 shadow">
              <h2 className="mb-4 text-lg font-semibold">Reason</h2>
              <p className="text-hpi-dark-gray whitespace-pre-wrap">
                {pto_request.reason}
              </p>
            </div>
          )}

          {/* Approval Info */}
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-lg font-semibold">Workflow</h2>
            <dl className="space-y-3">
              <div className="flex justify-between">
                <dt className="text-hpi-med-gray">Submitted</dt>
                <dd>{formatDateTime(pto_request.submitted_at)}</dd>
              </div>
              {pto_request.approved_at && (
                <>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">
                      {pto_request.status === 1 ? "Approved" : "Denied"}
                    </dt>
                    <dd>{formatDateTime(pto_request.approved_at)}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-hpi-med-gray">By</dt>
                    <dd>{pto_request.approved_by?.name ?? "-"}</dd>
                  </div>
                </>
              )}
            </dl>
          </div>

          {/* Approval Notes */}
          {pto_request.approval_notes && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Approval Notes</h2>
              <p className="text-hpi-dark-gray whitespace-pre-wrap">
                {pto_request.approval_notes}
              </p>
            </div>
          )}

          {/* Notes */}
          {pto_request.notes && (
            <div className="rounded-lg bg-white p-6 shadow md:col-span-2">
              <h2 className="mb-4 text-lg font-semibold">Notes</h2>
              <p className="text-hpi-dark-gray whitespace-pre-wrap">
                {pto_request.notes}
              </p>
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link
            href="/pto_requests"
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to PTO Requests
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
